/**
 * Created by piotr.pozniak@thebeaverhead.com on 23/10/2018.
 *
 * Rebuilt on BuilderShell per the design handoff
 * (assets/handoffs/design_handoff_new_calendar_page/README.md). This is
 * Phase 1 of the project plan: states 01-04 only (nothing selected /
 * template selected / validation error / ready). States 05-08 (creating,
 * no-connections empty state, quota-reached, server-failure) and the
 * content-swap-into-the-builder-on-create behaviour are deferred to later
 * phases — on success this still navigates to the unchanged CalendarPage.js,
 * same as before.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import PageTemplate from "./../templates/PageTemplate";

import BuilderShell from "../component/WidgetSettings/BuilderShell/BuilderShell";
import BuilderIdentityBand from "../component/WidgetSettings/BuilderIdentityBand/BuilderIdentityBand";
import TemplateStage from "./NewCalendarPage/TemplateStage";
import SetupPanel from "./NewCalendarPage/SetupPanel";
import Button from "../theme/untitled/Button/Button";
import { store } from "../store";
import { useCalendarStore } from "../../../hooks/redux/calendar";
import { useCalendarsStore } from "../../../hooks/redux/calendars";
import SuccessAlert from "../component/SuccessAlert";
import ApiError from "../component/ApiError";
import { calendarSettingsValidator } from "../tools";
import { CT, IT } from "../../../consts";
import { useIntegrationsStore } from "../../../hooks/redux/integrations";
import { isIntegrationHealthy } from "../consts/integrations";
import { getPublicWidgetUrl } from "../../../helpers/url";
import styles from "./NewCalendarPage.module.scss";

const IntegrationDefaultFilters = {
  [IT.google]: null,
};

const BUILDER_TABS = [
  { key: "setup", label: "Setup" },
  { key: "design", label: "Design", disabled: true },
  { key: "install", label: "Install", disabled: true },
];

const availableCalendars = [
  {
    title: "Detailed list",
    pitch:
      "Full descriptions, a sign-up link per event, and featured events called out.",
    tag: "Best for selling events",
    slug: CT.detailedList,
    thumbnailSrc: "/img/calendar_type/template-thumbs/detailed-list.svg",
    features: [
      "Can put CTA links to RSVP/Sign-up Forms on main page",
      "More visible highlight of Featured Events",
    ],
    defaultDesignSettings: {
      generalOpenDetailsModal: 1,
      generalExpandDetailAccording: 0,
      calendarShowShare: 1,
    },
    previewUrl: getPublicWidgetUrl("c/m6zJBjpm"),
    demoSlug: "detailed-list",
  },
  {
    title: "Cards (Slider)",
    pitch: "A carousel that shows a lot in a little space.",
    tag: "Best on mobile",
    slug: CT.cardSlider,
    thumbnailSrc: "/img/calendar_type/template-thumbs/cards-slider.svg",
    features: [
      "Ideal for those who want a carousel or presentation design",
      "Works great for the web and especially on mobile phones",
    ],
    defaultDesignSettings: {
      generalOpenDetailsModal: 1,
      generalExpandDetailAccording: 0,
      eventsNumberOption: "upcoming-limit",
      eventsNumber: 3,
      calendarShowShare: 1,
    },
    previewUrl: getPublicWidgetUrl("c/m6zJFDSh"),
    demoSlug: "cards-slider",
  },
  {
    title: "Cards (Grid)",
    pitch: "Social-style cards, several events at a glance.",
    tag: "Best on desktop",
    slug: CT.card,
    thumbnailSrc: "/img/calendar_type/template-thumbs/cards-grid.svg",
    features: [
      "At-a glance, multiple event full desktop display",
      "Creates excitement encouraging people to participate",
    ],
    defaultDesignSettings: {
      generalOpenDetailsModal: 1,
      generalExpandDetailAccording: 0,
      infiniteScroll: 1,
      calendarShowShare: 1,
    },
    previewUrl: getPublicWidgetUrl("c/m6zJGdzQ"),
    demoSlug: "cards-grid",
  },
  {
    title: "Cards (List)",
    pitch: "The same cards stacked — image and details in one compact row.",
    tag: "Best on mobile",
    slug: CT.cardList,
    thumbnailSrc: "/img/calendar_type/template-thumbs/cards-list.svg",
    features: [
      "Compact view can include both image and details",
      "Layout looks great on smaller digital screens",
    ],
    defaultDesignSettings: {
      generalOpenDetailsModal: 1,
      generalExpandDetailAccording: 0,
      infiniteScroll: 1,
      calendarShowShare: 1,
    },
    previewUrl: getPublicWidgetUrl("c/m6zJJEmH"),
    demoSlug: "cards-list",
  },
  {
    title: "Monthly view",
    pitch: "The whole month at once, so recurring events read as a pattern.",
    tag: "Best for recurring schedules",
    slug: CT.monthly,
    thumbnailSrc: "/img/calendar_type/template-thumbs/monthly-view.svg",
    features: [
      "Desktop: at-a-glance overview of recurring and featured events",
      "Mobile: easy access to daily events with minimal scrolling",
    ],
    defaultDesignSettings: {
      calendarShowShare: 1,
    },
    previewUrl: getPublicWidgetUrl("c/m6zJMzS9"),
    demoSlug: "monthly-view",
  },
  {
    title: "Weekly view",
    pitch: "Just this week, in list, pill or stacked form.",
    tag: "Best for planning attendance",
    slug: CT.weekly,
    thumbnailSrc: "/img/calendar_type/template-thumbs/weekly-view.svg",
    features: [
      "Easier for your people to plan their attendance",
      "Choice of 3 displays: list, pills, and stacks",
    ],
    defaultDesignSettings: {
      calendarShowShare: 1,
    },
    previewUrl: getPublicWidgetUrl("c/m6zJNFen"),
    demoSlug: "weekly-view",
  },
  {
    title: "Events list",
    pitch: "A plain, uncluttered list of what is on this month.",
    tag: "Simplest option",
    slug: CT.eventList,
    thumbnailSrc: "/img/calendar_type/template-thumbs/events-list.svg",
    features: [
      "Keep it simple to focus on the events themselves",
      "Recurring and Featured Badges show up well on desktop and mobile",
    ],
    defaultDesignSettings: {
      infiniteScroll: 1,
      calendarShowShare: 1,
    },
    previewUrl: getPublicWidgetUrl("c/m6zJPfFw"),
    demoSlug: "events-list",
  },
  {
    title: "Tiles",
    pitch: "Resizable tiles for a sidebar or a featured-events strip.",
    tag: "Best for sidebars",
    slug: CT.bubble,
    thumbnailSrc: "/img/calendar_type/template-thumbs/tiles.svg",
    features: [
      "Change the size and shape of the tiles to fit a variety of page layouts",
      "Can work as a sidebar widget or a featured events section.",
    ],
    defaultDesignSettings: {
      generalOpenDetailsModal: 1,
      generalExpandDetailAccording: 0,
      calendarShowShare: 1,
    },
    previewUrl: getPublicWidgetUrl("c/m6zJRuew"),
    demoSlug: "tiles",
  },
];

const initialState = {
  calendarModel: {
    name: "",
    available: 0,
    integration_uuid: 0,
    template: "",
    show_private_events: 0,
    featured_events_limit: 0,
  },
  errors: {},
};

const NewCalendarPage = () => {
  const { calendar, updateCalendar, setCalendar } = useCalendarStore();
  const { integrations } = useIntegrationsStore();
  const { calendars } = useCalendarsStore();

  const loading = calendar.update || calendar.fetch || calendar.delete;

  const history = useHistory();

  const [state, setState] = useState(JSON.parse(JSON.stringify(initialState)));
  const [submitted, setSubmitted] = useState(false);
  const [panelHidden, setPanelHidden] = useState(false);
  const onTogglePanel = useCallback(
    () => setPanelHidden((hidden) => !hidden),
    []
  );

  useEffect(() => {
    if (calendar.fetchSuccess && !calendar.model.integration) {
      history.push("/dashboard");
    }

    if (calendars.fetchSuccess) {
      setTimeout(updateJquery, 200);
    }
  }, [calendar.fetchSuccess, calendars.fetchSuccess]);

  // The page itself never scrolls (AppShell is height:100vh) — the actual
  // scrolling element is the shell's content column. A long template list
  // can leave the user scrolled well past the name field once they pick
  // one, so bring them back to it and drop focus straight into the name
  // input to keep the "select template → name it" flow moving.
  useEffect(() => {
    document
      .querySelector("[data-app-content]")
      ?.scrollTo({ top: 0, behavior: "smooth" });
    if (state.calendarModel.template) {
      document.getElementById("calendar_name")?.focus();
    }
  }, [state.calendarModel.template]);

  useEffect(() => {
    return () => setCalendar(null);
  }, []);

  const integrationOptions = useMemo(
    () =>
      integrations.collection
        .filter((i) => Number.parseInt(i.available))
        .filter((i) => Object.values(IT).includes(i.type))
        .map((i) => ({
          uuid: i.uuid,
          label: `${i.name} — ${i.username}`,
          healthy: isIntegrationHealthy(i),
        })),
    [integrations.collection]
  );

  const selectedTemplate = availableCalendars.find(
    (t) => t.slug === state.calendarModel.template
  );

  const untitled = !state.calendarModel.name;
  const bandName = state.calendarModel.name || "Untitled calendar";

  const onUpdateField = useCallback((fieldName, value) => {
    setState((prev) => {
      const calendarModel = { ...prev.calendarModel, [fieldName]: value };
      const errorKey =
        fieldName === "name"
          ? "nameError"
          : fieldName === "integration_uuid"
          ? "integrationError"
          : null;
      const errors = { ...prev.errors };
      if (errorKey) {
        delete errors[errorKey];
      }
      return { calendarModel, errors };
    });
  }, []);

  const onChangeName = useCallback(
    (value) => onUpdateField("name", value),
    [onUpdateField]
  );
  const onChangeIntegration = useCallback(
    (uuid) => onUpdateField("integration_uuid", uuid),
    [onUpdateField]
  );

  const onSelectTemplate = useCallback(
    (slug) => {
      setCalendar({});
      setState((prev) => ({
        ...prev,
        calendarModel: { ...prev.calendarModel, template: slug },
      }));
    },
    [setCalendar]
  );

  const onBackToGrid = useCallback(() => {
    setState((prev) => ({
      ...prev,
      calendarModel: { ...prev.calendarModel, template: "" },
    }));
  }, []);

  const cancelSelection = useCallback(() => {
    setCalendar({});
    setState(JSON.parse(JSON.stringify(initialState)));
    setSubmitted(false);
  }, [setCalendar]);

  const onSubmit = useCallback(async () => {
    setSubmitted(true);
    const errors = calendarSettingsValidator(state.calendarModel);
    if (Object.keys(errors).length) {
      setState((prev) => ({ ...prev, errors }));
      return;
    }

    const data = { ...state.calendarModel };
    data.widget_settings = { design: selectedTemplate.defaultDesignSettings };

    const selectedIntegration = integrations.collection.find(
      (i) => i.uuid === state.calendarModel.integration_uuid
    );

    // for PCO, turn private events on by default
    if (IntegrationDefaultFilters[selectedIntegration.type]) {
      data.filter_settings = {
        filters: IntegrationDefaultFilters[selectedIntegration.type],
        featured_filters: IntegrationDefaultFilters[selectedIntegration.type],
      };
    }

    await updateCalendar(null, data);

    const storeState = store.getState();
    if (storeState.calendar.updateSuccess) {
      await setCalendar(null);
      history.push("/calendar/" + storeState.calendar.model.uuid);
    }
  }, [
    state.calendarModel,
    selectedTemplate,
    integrations.collection,
    updateCalendar,
    setCalendar,
    history,
  ]);

  const panelBody = (
    <SetupPanel
      templateTitle={selectedTemplate?.title}
      onChangeTemplate={onBackToGrid}
      name={state.calendarModel.name}
      integrationUuid={state.calendarModel.integration_uuid}
      errors={state.errors}
      submitted={submitted}
      integrationOptions={integrationOptions}
      onChangeName={onChangeName}
      onChangeIntegration={onChangeIntegration}
      disabled={loading}
    />
  );

  const footer = selectedTemplate ? (
    <div className={styles.footerContent}>
      <SuccessAlert>
        {calendar.updateSuccess ? "Your changes have been saved." : null}
      </SuccessAlert>
      {/* Full state-08 panel error block (README §5) lands in Phase 3; this
          keeps the existing ApiError as a functional fallback for now. */}
      <ApiError
        error={calendar.updateError}
        defaultErrorMessage={"Could not save your settings. Please try again"}
      />
      <div className={styles.footerButtons}>
        <Button hierarchy="tertiary" onClick={cancelSelection}>
          Cancel
        </Button>
        <Button
          hierarchy="primary"
          loading={loading}
          className={styles.primaryButton}
          onClick={onSubmit}
        >
          Create calendar
        </Button>
      </div>
    </div>
  ) : null;

  return (
    <PageTemplate
      sidebarId="calendar"
      className={"page-wrapper-widget"}
      header={
        <BuilderIdentityBand
          name={bandName}
          untitled={untitled}
          published={false}
          indexHref="/calendars"
          indexLabel="Calendars"
          previewDisabled
          panelHidden={panelHidden}
          onTogglePanel={onTogglePanel}
        />
      }
    >
      <BuilderShell
        stage={
          <TemplateStage
            templates={availableCalendars}
            selectedSlug={state.calendarModel.template}
            onSelectTemplate={onSelectTemplate}
            onBackToGrid={onBackToGrid}
          />
        }
        tabs={BUILDER_TABS}
        activeTab="setup"
        panelBody={panelBody}
        footer={footer}
        panelHidden={panelHidden}
      />
    </PageTemplate>
  );
};

export default NewCalendarPage;
